import React, { useRef, useEffect } from 'react';
import { useInputState, useDebouncedValue } from '@mantine/hooks';
import { Container, Input, Icon } from './Search.styles';

export interface SearchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  inline?: boolean;
  onSearchChange?: (searchValue: string) => void;
}

export function Search({
  placeholder = 'Search...',
  inline,
  className,
  ...props
}: SearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchInputValue, setSearchInputValue] = useInputState(props.value ?? '');
  const [debouncedSearchInputValue] = useDebouncedValue(searchInputValue, 400);

  useEffect(() => {
    if (props.onSearchChange) {
      props.onSearchChange(`${debouncedSearchInputValue}`);
    }
  }, [debouncedSearchInputValue]);

  // Although we want this component to be uncontroller, sometimes the "Initial Value" changes
  // when the URL changes, so we need to make sure the *displayed value* is correct
  useEffect(() => {
    if (
      props.value !== undefined &&
      inputRef.current &&
      props.value !== searchInputValue
    ) {
      setSearchInputValue(props.value);
    }
  }, [props.value]);

  // This is backwards compatibility for pages that haven't implemented the
  // performance refactors yet
  const inputElement = props.onChange ? (
    <Input
      ref={inputRef}
      type="text"
      onChange={props.onChange}
      placeholder={placeholder}
      value={props.value}
      aria-label={placeholder}
      role="searchbox"
    />
  ) : (
    <Input
      ref={inputRef}
      type="text"
      onChange={setSearchInputValue}
      placeholder={placeholder}
      value={searchInputValue}
      aria-label={placeholder}
      role="searchbox"
    />
  );

  return (
    <Container
      $hasContent={searchInputValue !== ''}
      $inline={inline}
      className={className}
      role="search"
      aria-label="Search"
    >
      <Icon icon="other.search" size={'20px'} aria-hidden="true" />
      {inputElement}
    </Container>
  );
}
